// ─── §4 Organization — PDCA stage auto-advance ──────────────────────────────
//
// Trigger conditions from GRC_ARCHITECTURE.md §4 Organization.pdcaStage:
//   Plan → Do:     Policy Document status=Approved
//                  AND SoA approvalDate set
//                  AND RTP approvalDate set
//                  AND Scope approvalDate set
//                  AND Context reviewDate set
//   Do → Check:    ≥ 1 Internal Audit with status=Completed
//   Check → Act:   ≥ 1 ManagementReview with ≥ 1 decision
//   Act → Plan:    certificationStatus = "surveillance"
//
// Auto-advance never demotes: if the user has manually moved pdcaStage
// forward past the computed stage (via Settings), their choice stands.
// The only backward movement is the Act → Plan cycle reset that fires
// when certificationStatus is "surveillance" and the record is currently
// in Act — this models the start of a new PDCA cycle per spec.

import {
  listOrgCollection,
  readOrgFile,
  writeOrgFile,
} from '../storage/FileSystemAdapter';

const STAGE_ORDER = { Plan: 0, Do: 1, Check: 2, Act: 3 };

async function gatherPdcaContext(orgId, profile) {
  const [soa, rtp, scope, context, documents, audits, mrs] = await Promise.all([
    readOrgFile(orgId, 'controls/soa.json').catch(() => null),
    readOrgFile(orgId, 'rtp.json').catch(() => null),
    readOrgFile(orgId, 'scope.json').catch(() => null),
    readOrgFile(orgId, 'context.json').catch(() => null),
    listOrgCollection(orgId, 'documents').catch(() => []),
    listOrgCollection(orgId, 'audits').catch(() => []),
    listOrgCollection(orgId, 'management-reviews').catch(() => []),
  ]);

  const policyApproved = (documents || []).some(
    (d) => d && d.isInformationSecurityPolicy === true && d.status === 'Approved' && d.approvalDate
  );
  const soaApproved     = !!(soa && soa.approvalDate);
  const rtpApproved     = !!(rtp && rtp.approvalDate);
  const scopeApproved   = !!(scope && scope.approvalDate);
  const contextReviewed = !!(context && context.reviewDate);

  const planPrereqsMet =
    policyApproved && soaApproved && rtpApproved && scopeApproved && contextReviewed;

  const hasCompletedInternalAudit = (audits || []).some(
    (a) => a && a.type === 'Internal' && a.status === 'Completed'
  );

  const hasMRWithDecision = (mrs || []).some(
    (mr) => mr && Array.isArray(mr.decisions) && mr.decisions.length >= 1
  );

  const certificationStatus = (profile && profile.certificationStatus) || 'none';

  return {
    planPrereqsMet,
    hasCompletedInternalAudit,
    hasMRWithDecision,
    certificationStatus,
  };
}

export function computeAutoAdvancedPdcaStage(currentStage, ctx) {
  const current = currentStage || 'Plan';

  // Act → Plan cycle reset: when the org enters surveillance while in Act,
  // the PDCA cycle restarts. Subsequent evaluations can re-advance forward
  // as new deliverables land in the new cycle.
  if (current === 'Act' && ctx.certificationStatus === 'surveillance') {
    return 'Plan';
  }

  // Max stage eligible given deliverables (forward ladder).
  let eligible = 'Plan';
  if (ctx.planPrereqsMet)                                              eligible = 'Do';
  if (eligible === 'Do'    && ctx.hasCompletedInternalAudit)           eligible = 'Check';
  if (eligible === 'Check' && ctx.hasMRWithDecision)                   eligible = 'Act';

  // Never demote — the manual Settings select is the only way to move back.
  if (STAGE_ORDER[eligible] > STAGE_ORDER[current]) return eligible;
  return current;
}

// Evaluate, and if the stage should advance, write the updated profile and
// return it. If no advancement, returns the input profile unchanged.
// `onPersistedUpdate` is an optional callback invoked with the new profile
// when a write happens, so the caller can sync in-memory state.
export async function maybeAdvancePdcaStage(orgId, profile, onPersistedUpdate) {
  if (!profile || !orgId) return profile;
  const current = profile.pdcaStage || 'Plan';
  const ctx = await gatherPdcaContext(orgId, profile);
  const next = computeAutoAdvancedPdcaStage(current, ctx);
  if (next === current) return profile;

  const now = new Date().toISOString();
  const changedBy = profile.consultantPersonId || 'system';
  const logEntry = {
    timestamp: now,
    field: 'pdcaStage',
    previousValue: current,
    newValue: next,
    changedBy,
  };
  const updated = {
    ...profile,
    pdcaStage: next,
    updatedAt: now,
    changeLog: [
      ...(Array.isArray(profile.changeLog) ? profile.changeLog : []),
      logEntry,
    ],
  };
  try {
    await writeOrgFile(orgId, 'profile.json', updated);
    if (typeof onPersistedUpdate === 'function') onPersistedUpdate(updated);
    return updated;
  } catch {
    // Auto-advance is best-effort — a write failure leaves the stage as-is
    // and will be retried on next evaluation.
    return profile;
  }
}
